import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DepthChart } from "../../../types/DepthChartTypes";
import { useDepthChart } from "../../../contexts/DepthChartContext";
import { useSchemes } from "../../../contexts/SchemesContext";
import { DeleteConfirmationPopover } from "./DeleteConfirmationPopover";
import { AddToGameDialog } from "./AddToGameDialog";

interface SavedChartsListProps {
  isLoading: boolean;
  error: string | null;
  onCreateNew: () => void;
  onSelectDepthChart: (depthChart: DepthChart) => void;
}

export const SavedChartsList: React.FC<SavedChartsListProps> = ({
  isLoading,
  error,
  onCreateNew,
  onSelectDepthChart,
}) => {
  const {
    deleteDepthChart,
    selectedDepthChart,
    selectedSide,
    templateDepthChart,
    depthCharts,
    addDepthChartToGame,
    fetchDepthCharts,
  } = useDepthChart();
  const { personnel } = useSchemes();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [chartToDelete, setChartToDelete] = useState<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChartId, setSelectedChartId] = useState<number | null>(null);
  const [addToGameDialogOpen, setAddToGameDialogOpen] = useState(false);
  const [chartToAddToGame, setChartToAddToGame] = useState<number | null>(null);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    chartId: number
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    console.log("handleMenuClick", chartId);
    setSelectedChartId(chartId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedChartId(null);
  };

  const handleDeleteClick = () => {
    if (selectedChartId) {
      setChartToDelete(selectedChartId);
      setAnchorEl(menuAnchorEl);
      setDeleteConfirmationOpen(true);
      handleMenuClose();
    }
  };

  const handleAddToGame = () => {
    if (selectedChartId) {
      setChartToAddToGame(selectedChartId);
      setAddToGameDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDeleteConfirm = async () => {
    if (chartToDelete) {
      await deleteDepthChart(chartToDelete);
      setDeleteConfirmationOpen(false);
      setAnchorEl(null);
      setChartToDelete(null);
    }
  };

  const handleAddToGameConfirm = (gameId: number) => {
    if (chartToAddToGame) {
      addDepthChartToGame(chartToAddToGame, gameId);
      setAddToGameDialogOpen(false);
      setChartToAddToGame(null);
    }
  };

  // If loading, show a loading message
  if (isLoading) {
    return (
      <Box
        sx={{
          bgcolor: "#1e1e1e",
          borderRadius: 2,
          p: 2,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Saved Charts
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.7)", p: 2, textAlign: "center" }}
        >
          Loading depth charts...
        </Typography>
      </Box>
    );
  }

  // If error, show an error message
  if (error) {
    return (
      <Box
        sx={{
          bgcolor: "#1e1e1e",
          borderRadius: 2,
          p: 2,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Saved Charts
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.7)", p: 2, textAlign: "center" }}
        >
          Error loading depth charts: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#1e1e1e",
        borderRadius: 2,
        p: 2,
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Stack spacing={2} sx={{ overflow: "auto" }}>
        {/* Create New Button - Always shown first */}
        <Button
          fullWidth
          variant="contained"
          onClick={onCreateNew}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: "rgba(123, 45, 58, 0.9)",
            color: "white",
            "&:hover": {
              bgcolor: "rgba(123, 45, 58, 1)",
            },
            py: 1.5,
            mb: 1,
          }}
        >
          Create New Depth Chart
        </Button>

        {/* Template Chart - Always shown second */}
        {templateDepthChart && (
          <Card
            sx={{
              bgcolor:
                selectedDepthChart?.id === templateDepthChart.id
                  ? "rgba(123, 45, 58, 0.5)"
                  : "rgba(40, 40, 40, 0.8)",
              color: "white",
              border:
                selectedDepthChart?.id === templateDepthChart.id
                  ? "1px solid rgba(123, 45, 58, 0.9)"
                  : "1px solid rgba(255, 255, 255, 0.12)",
              "&:hover": {
                bgcolor: "rgba(60, 60, 60, 0.9)",
              },
            }}
          >
            <CardActionArea
              onClick={() => onSelectDepthChart(templateDepthChart)}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Template Chart
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    New Depth Charts will be based on this template. You can
                    edit position titles and position locations
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        )}

        {/* Show "No saved charts" message if there are no charts */}
        {depthCharts.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.7)", p: 2, textAlign: "center" }}
          >
            No saved depth charts for {selectedSide.toLowerCase()}
          </Typography>
        ) : (
          // Otherwise show the saved charts
          depthCharts.map((chart) => (
            <Card
              key={chart.id}
              sx={{
                bgcolor:
                  selectedDepthChart?.id === chart.id
                    ? "rgba(123, 45, 58, 0.5)"
                    : "rgba(40, 40, 40, 0.8)",
                color: "white",
                border:
                  selectedDepthChart?.id === chart.id
                    ? "1px solid rgba(123, 45, 58, 0.9)"
                    : "1px solid rgba(255, 255, 255, 0.12)",
                "&:hover": {
                  bgcolor: "rgba(60, 60, 60, 0.9)",
                },
                position: "relative",
              }}
            >
              <Tooltip title="Actions">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, chart.id!)}
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      color: "rgba(255, 255, 255, 1)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    zIndex: 1,
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                <MenuItem onClick={handleAddToGame}>Add to Game</MenuItem>
              </Menu>
              <CardActionArea onClick={() => onSelectDepthChart(chart)}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {chart.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      mt: 1,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        Created:{" "}
                        <span
                          style={{
                            fontWeight: "bold",
                            color: "rgba(255,255,255,0.9)",
                          }}
                        >
                          {chart.created_at
                            ? new Date(chart.created_at).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </Typography>
                      {chart.personnel_group && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            textAlign: "left",
                            width: "100%",
                          }}
                        >
                          Personnel Group:{" "}
                          <span
                            style={{
                              fontWeight: "bold",
                              color: "rgba(255,255,255,0.9)",
                            }}
                          >
                            {personnel.find(
                              (p) => p.id === chart.personnel_group
                            )?.name || "Unknown"}
                          </span>
                        </Typography>
                      )}
                      {chart.game && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            textAlign: "left",
                            width: "100%",
                          }}
                        >
                          Game:{" "}
                          <span
                            style={{
                              fontWeight: "bold",
                              color: "rgba(255,255,255,0.9)",
                            }}
                          >
                            {chart.game.opponent_club.name}
                          </span>
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))
        )}
      </Stack>

      {/* Add to Game Dialog */}
      <AddToGameDialog
        open={addToGameDialogOpen}
        onClose={() => {
          setAddToGameDialogOpen(false);
          setChartToAddToGame(null);
        }}
        onConfirm={handleAddToGameConfirm}
        chartName={
          chartToAddToGame
            ? depthCharts.find((c) => c.id === chartToAddToGame)?.name ||
              "this chart"
            : "this chart"
        }
      />

      {/* Delete Confirmation Popover */}
      <DeleteConfirmationPopover
        open={deleteConfirmationOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setDeleteConfirmationOpen(false);
          setAnchorEl(null);
          setChartToDelete(null);
        }}
        onDelete={handleDeleteConfirm}
        chartName={
          chartToDelete
            ? depthCharts.find((c) => c.id === chartToDelete)?.name ||
              "this chart"
            : "this chart"
        }
      />
    </Box>
  );
};
